import { useMemo, useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TextField,
  MenuItem,
  Button,
  Stack,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
} from '@mui/material';
import type { SessionSummaryDto } from '@replayops/contracts';
import { useMe, useSessions, useSearchSessions } from '../api/hooks.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

interface Filters {
  email: string;
  url: string;
  browser: string;
  os: string;
  country: string;
  customEvent: string;
  minDurationS: string;
  maxDurationS: string;
  from: string;
  to: string;
  hasError: boolean;
  hasRageClick: boolean;
  hasDeadClick: boolean;
  hasConsoleError: boolean;
  excludeBots: boolean;
}

const EMPTY: Filters = {
  email: '',
  url: '',
  browser: '',
  os: '',
  country: '',
  customEvent: '',
  minDurationS: '',
  maxDurationS: '',
  from: '',
  to: '',
  hasError: false,
  hasRageClick: false,
  hasDeadClick: false,
  hasConsoleError: false,
  excludeBots: true,
};

/** Builds the search DSL from the form, omitting empty/false fields. */
function toDsl(f: Filters): Record<string, unknown> {
  const dto: Record<string, unknown> = { limit: 100, offset: 0 };
  if (f.email) dto.email = f.email;
  if (f.url) dto.url = f.url;
  if (f.browser) dto.browser = f.browser;
  if (f.os) dto.os = f.os;
  if (f.country) dto.country = f.country;
  if (f.customEvent) dto.customEvent = f.customEvent;
  if (f.minDurationS) dto.minDurationMs = Math.round(Number(f.minDurationS) * 1000);
  if (f.maxDurationS) dto.maxDurationMs = Math.round(Number(f.maxDurationS) * 1000);
  if (f.from) dto.from = new Date(f.from).toISOString();
  if (f.to) dto.to = new Date(f.to).toISOString();
  if (f.hasError) dto.hasError = true;
  if (f.hasRageClick) dto.hasRageClick = true;
  if (f.hasDeadClick) dto.hasDeadClick = true;
  if (f.hasConsoleError) dto.hasConsoleError = true;
  if (f.excludeBots) dto.excludeBots = true;
  return dto;
}

function activeCount(f: Filters): number {
  // excludeBots defaults on and isn't a "search narrowing" the user set — don't
  // count it toward the active-filter badge.
  return Object.entries(f)
    .filter(([k]) => k !== 'excludeBots')
    .filter(([, v]) => (typeof v === 'boolean' ? v : v !== '')).length;
}

export function SessionsPage() {
  const t = useT().sessions;
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { data: listed } = useSessions(orgId, projectId);
  const search = useSearchSessions(orgId, projectId);

  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [result, setResult] = useState<{ data: SessionSummaryDto[]; total: number } | null>(null);

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setFilters((s) => ({ ...s, [key]: value }));

  // Populate the dropdowns from the distinct values in the recent listing.
  const distinct = (key: 'browser' | 'os' | 'country') =>
    [...new Set((listed ?? []).map((s) => s[key]).filter(Boolean) as string[])].sort();
  const browsers = useMemo(() => distinct('browser'), [listed]);
  const oses = useMemo(() => distinct('os'), [listed]);
  const countries = useMemo(() => distinct('country'), [listed]);

  const sort = useSort<'user' | 'url' | 'browserOs' | 'duration' | 'actions' | 'started'>('started');
  const rows = sort.sorted(result?.data ?? listed ?? [], {
    user: (s) => s.userEmail ?? s.userId ?? s.anonymousId,
    url: (s) => s.lastUrl,
    browserOs: (s) => `${s.browser} ${s.os}`,
    duration: (s) => s.durationMs,
    actions: (s) => s.interactionCount ?? 0,
    started: (s) => s.startedAt,
  });
  const nActive = activeCount(filters);

  const apply = async () => setResult(await search.mutateAsync(toDsl(filters)));
  const clear = () => {
    setFilters(EMPTY);
    setResult(null);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5">{t.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {result ? t.search.matches(result.total) : t.search.recent(rows.length)}
        </Typography>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
          }}
        >
          <TextField size="small" label={t.search.userEmail} value={filters.email} onChange={(e) => set('email', e.target.value)} />
          <TextField size="small" label={t.search.urlContains} value={filters.url} onChange={(e) => set('url', e.target.value)} />
          <TextField select size="small" label={t.filters.browser} value={filters.browser} onChange={(e) => set('browser', e.target.value)}>
            <MenuItem value="">{t.search.any}</MenuItem>
            {browsers.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
          </TextField>
          <TextField select size="small" label={t.filters.os} value={filters.os} onChange={(e) => set('os', e.target.value)}>
            <MenuItem value="">{t.search.any}</MenuItem>
            {oses.map((o) => (<MenuItem key={o} value={o}>{o}</MenuItem>))}
          </TextField>
          <TextField select size="small" label={t.search.country} value={filters.country} onChange={(e) => set('country', e.target.value)}>
            <MenuItem value="">{t.search.any}</MenuItem>
            {countries.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
          </TextField>
          <TextField size="small" label={t.search.customEvent} value={filters.customEvent} onChange={(e) => set('customEvent', e.target.value)} />
          <TextField size="small" type="number" label={t.search.minDuration} value={filters.minDurationS} onChange={(e) => set('minDurationS', e.target.value)} />
          <TextField size="small" type="number" label={t.search.maxDuration} value={filters.maxDurationS} onChange={(e) => set('maxDurationS', e.target.value)} />
          <TextField size="small" type="datetime-local" label={t.search.from} InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => set('from', e.target.value)} />
          <TextField size="small" type="datetime-local" label={t.search.to} InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => set('to', e.target.value)} />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="center" flexWrap="wrap" justifyContent="space-between" spacing={1}>
          <Stack direction="row" flexWrap="wrap">
            <FormControlLabel control={<Checkbox size="small" checked={filters.hasError} onChange={(e) => set('hasError', e.target.checked)} />} label={t.search.errors} />
            <FormControlLabel control={<Checkbox size="small" checked={filters.hasRageClick} onChange={(e) => set('hasRageClick', e.target.checked)} />} label={t.search.rageClicks} />
            <FormControlLabel control={<Checkbox size="small" checked={filters.hasDeadClick} onChange={(e) => set('hasDeadClick', e.target.checked)} />} label={t.search.deadClicks} />
            <FormControlLabel control={<Checkbox size="small" checked={filters.hasConsoleError} onChange={(e) => set('hasConsoleError', e.target.checked)} />} label={t.search.consoleErrors} />
            <FormControlLabel control={<Checkbox size="small" checked={filters.excludeBots} onChange={(e) => set('excludeBots', e.target.checked)} />} label={t.filters.hideCrawlers} />
          </Stack>
          <Stack direction="row" spacing={1}>
            {nActive > 0 && <Button onClick={clear}>{t.filters.clear(nActive)}</Button>}
            <Button variant="contained" onClick={apply} disabled={search.isPending}>
              {search.isPending ? t.search.searching : t.search.applyFilters}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {!result && listed?.length === 0 && (
        <Paper sx={{ p: 3, mb: 2, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>{t.search.emptyTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t.search.emptyBody}
          </Typography>
          <Button variant="contained" onClick={() => navigate(`/projects/${projectId}/api-keys`)}>
            {t.search.installSdk}
          </Button>
        </Paper>
      )}

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {sort.head('user', t.columns.user)}
              {sort.head('url', t.search.url)}
              {sort.head('browserOs', t.columns.browserOs)}
              {sort.head('duration', t.columns.duration)}
              {sort.head('actions', t.columns.actions, 'right')}
              <TableCell>{t.columns.signals}</TableCell>
              {sort.head('started', t.columns.started)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s) => (
              <TableRow
                hover
                key={s.id}
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/projects/${projectId}/sessions/${s.id}`)}
              >
                <TableCell>{s.userEmail ?? s.userId ?? s.anonymousId.slice(0, 12)}</TableCell>
                <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.lastUrl}
                </TableCell>
                <TableCell>
                  {s.browser} / {s.os}
                </TableCell>
                <TableCell>{fmtDuration(s.durationMs)}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t.actionsHint}>
                    <span>{(s.interactionCount ?? 0).toLocaleString()}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {s.hasError && <Chip label={t.signals.error} color="error" size="small" />}
                    {s.hasRageClick && <Chip label={t.signals.rage} color="warning" size="small" />}
                    {s.hasDeadClick && <Chip label={t.signals.dead} size="small" />}
                    {s.isBounce && <Chip label={t.signals.bounce} size="small" variant="outlined" />}
                    {s.isBot && <Chip label={t.signals.bot} size="small" color="default" variant="outlined" sx={{ opacity: 0.7 }} />}
                  </Stack>
                </TableCell>
                <TableCell>{formatDateTime(s.startedAt)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t.empty}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
